<template>
  <div>
    <div
      :class="`flex flex-col gap-4 timeline-event-content-tooltip max-w-72 timeline-event-tooltip-content-${event.id}`"
    >
      <div class="flex flex-col gap-1">
        <div class="flex items-center justify-between gap-2">
          <div class="flex flex-col">
            {{ appointment.service_category_name }}
          </div>
          <div
            class="rounded-lg px-2 py-1 text-xs text-gray-100 bg-[#313F52] text-right"
          >
            {{ capitalize(appointment.priority) }}
          </div>
        </div>
        <hr class="w-full border-t-1 border-gray-500 my-2" />
        <div class="flex items-start justify-between text-xs">
          <div class="flex-1 text-white">Revenue</div>
          <div
            v-if="appointment.job_invoice_revenue_cents !== null"
            class="text-green-400 flex-1 text-right"
          >
            {{ dollars(appointment.job_invoice_revenue_cents) }}
          </div>
          <div v-else class="flex-1 text-gray-300 text-right">--</div>
        </div>
        <div class="flex items-start justify-between text-xs">
          <div class="flex-1 text-white">{{ technicianLabel }}</div>
          <div class="flex-1 text-gray-300 text-right">
            {{ technicianNames }}
          </div>
        </div>
        <hr class="w-full border-t-1 border-gray-500 my-2" />
        <div class="flex items-start justify-between text-xs">
          <div class="flex-1 text-white">Customer</div>
          <div
            class="flex-1 inline-flex items-center gap-1 text-gray-300 justify-end"
          >
            <CrownSimple
              v-if="appointment.customer.membership"
              :class="['h-3']"
            />
            <span data-cy="popover-customer-name">{{ customerName }}</span>
          </div>
        </div>
        <div class="flex items-start justify-between text-xs">
          <div class="flex-1 text-white">Appointment</div>
          <div class="flex-1 text-gray-300 text-right">
            {{ scheduledTimeLabel }}
          </div>
        </div>
        <div class="flex items-start justify-between text-xs">
          <div class="flex-1 text-white">Actual Time</div>
          <div class="flex-1 text-gray-300 text-right">
            {{ actualTimeLabel }}
          </div>
        </div>
        <div class="flex items-start justify-between text-xs">
          <div class="flex-1 text-white">Location</div>
          <div class="flex-1 text-gray-300 text-right">
            {{ fullAddress(appointment.address) }}
          </div>
        </div>
        <div class="flex items-start justify-between text-xs">
          <div class="flex-1 text-white">Phone</div>
          <div class="flex-1 text-gray-300 text-right">
            {{ appointment.customer.phone }}
          </div>
        </div>
        <hr v-if="hasTags" class="w-full border-t-1 border-gray-500 my-2" />
        <div
          v-if="appointment.customer.tags?.length"
          class="flex items-start justify-between text-xs gap-1"
        >
          <div class="flex-1 text-white">Customer Tags</div>
          <TagList
            class="flex-1 justify-end"
            :tags="appointment.customer.tags"
            :editable="false"
            :removeable="false"
            :click-emit="''"
          />
        </div>
        <div
          v-if="appointment.job_tags?.length"
          class="flex items-start justify-between text-xs gap-1"
        >
          <div class="flex-1 text-white">Job Tags</div>
          <TagList
            class="flex-1 justify-end"
            :tags="appointment.job_tags"
            :editable="false"
            :removeable="false"
            :click-emit="''"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { customerHeaderName } from "@/lib/customerTypes";
import { customDateFormatWithTimezone } from "@/lib/helpers";

import CrownSimple from "@/images/CrownSimple.svg?component";
import { capitalize, dollars, fullAddress } from "@/mixins";
import TagList from "@/Tags/TagList.vue";
import { AppointmentEvent } from "@/typings/scheduler";

const props = defineProps<{
  event: AppointmentEvent;
}>();
const originalDetails = computed(() => props.event.original.details);
const appointment = computed(() => originalDetails.value.appointment);
const customerName = computed(() =>
  customerHeaderName(
    appointment.value.customer.full_name,
    appointment.value.customer.company_name,
    appointment.value.customer.customer_type
  )
);
const scheduledTimeLabel = computed(() => {
  return `${customDateFormatWithTimezone(
    appointment.value.arrival_window_start_time,
    "h:mmA",
    window.currentTenant.timezone
  )} - ${customDateFormatWithTimezone(
    appointment.value.arrival_window_end_time,
    "h:mmA",
    window.currentTenant.timezone
  )}`;
});
const actualTimeLabel = computed(() => {
  const inputFormat = props.event.start.indexOf("M") > -1 ? "h:mm A" : "";
  return `${customDateFormatWithTimezone(
    props.event.start,
    "h:mmA",
    window.currentTenant.timezone,
    inputFormat
  )} - ${customDateFormatWithTimezone(
    props.event.end,
    "h:mmA",
    window.currentTenant.timezone,
    inputFormat
  )}`;
});
const technicianNames = computed(() => {
  const techs = originalDetails.value.technicians;
  if (techs?.length) {
    return techs.map((t: { full_name: string }) => t.full_name).join(", ");
  }
  return originalDetails.value.technician?.full_name || "--";
});
const technicianLabel = computed(() => {
  const techs = originalDetails.value.technicians;
  return techs?.length > 1 ? "Technicians" : "Technician";
});
const hasTags = computed(
  () =>
    appointment.value.customer.tags?.length ||
    appointment.value.job_tags?.length
);
</script>

<style scoped></style>
